import { Button } from '@/components/common/Button';
import { FlexRow } from '@/components/common/Flex';
import { OverlappingCircles, User03 } from '@/components/common/icons';
import {
    Box,
    FlexProps,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Portal,
    Text,
} from '@chakra-ui/react';
import { usePageContext } from '@common/react/pageMode';
import { PageMode } from '@common/types';
import pluralize from '@common/util/pluralize';
import { FC } from 'react';
import Toggle, { ToggleOption } from '../Toggle';

export const pageModeInfoMap: Record<
    PageMode,
    { value: PageMode; label: string; el: React.ReactNode }
> = {
    PUBLIC: {
        value: 'PUBLIC',
        label: 'What’s ours',
        el: <OverlappingCircles size="sm" />,
    },
    MINE: {
        value: 'MINE',
        label: 'What’s mine',
        el: <User03 size="sm" />,
    },
};

interface PageModeToggleProps extends Omit<FlexProps, 'transition'> {
    value: PageMode;
    onChanged?: (value: PageMode) => void;
}

const PageModeToggle: FC<PageModeToggleProps> = ({
    value,
    onChanged,
    ...props
}) => {
    const { popover, hidePopover } = usePageContext();
    const { isOpen, hiddenAccountsCount, hiddenAccountsType } = popover;

    const options: [ToggleOption<PageMode>, ToggleOption<PageMode>] = [
        pageModeInfoMap.PUBLIC,
        pageModeInfoMap.MINE,
    ];

    return (
        <Popover
            isOpen={isOpen}
            onClose={hidePopover}
            placement="right"
            closeOnBlur={true}
            gutter={-40}
        >
            <PopoverTrigger>
                <FlexRow>
                    <Toggle
                        {...props}
                        value={value}
                        onChanged={(v) => onChanged?.(v)}
                        options={options}
                        data-cy="page-mode-toggle"
                    />
                </FlexRow>
            </PopoverTrigger>
            <Portal>
                <Box zIndex={400} w="full" h="full" position="relative">
                    <PopoverContent p={3}>
                        <PopoverArrow />
                        <PopoverHeader>
                            <Text fontWeight="medium" fontSize="md">
                                Your{' '}
                                {pluralize(
                                    `${hiddenAccountsType} account`,
                                    hiddenAccountsCount,
                                    true
                                )}{' '}
                                {hiddenAccountsCount === 1 ? 'is' : 'are'} only
                                visible in{' '}
                                {value === 'PUBLIC'
                                    ? "What's mine"
                                    : "What's ours"}{' '}
                                mode.
                            </Text>
                        </PopoverHeader>
                        <PopoverBody>
                            <Text mb={4} fontSize="md">
                                To view{' '}
                                {hiddenAccountsCount === 1 ? 'it' : 'them'},
                                just click this toggle to switch between
                                &quot;What&apos;s mine&quot; and
                                &quot;What&apos;s ours&quot; mode.
                            </Text>
                            <Button
                                size="sm"
                                onClick={hidePopover}
                                onClickEvent="Page Mode Toggle Popover Got It Button Clicked"
                            >
                                Got it
                            </Button>
                        </PopoverBody>
                    </PopoverContent>
                </Box>
            </Portal>
        </Popover>
    );
};

export default PageModeToggle;
